import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_TYPE_OF_PAYMENTS,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import * as z from 'zod';
import TypeOfPaymentGenerateButton from '../ui/CustomComponents/TypeOfPaymentGenerateButton';

type TypeOfPayment = Prisma.$TypeOfPaymentPayload['scalars'];

export type ITypeOfPayment = TypeOfPayment &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'>;

export interface ITypeOfPaymentFetchParams extends IListFetchParams {
  hasEndOfMonth?: boolean;
}

class TypOfPaymentRepository extends BaseRepository<
  ITypeOfPayment,
  ITypeOfPaymentFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['name'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'daysToFirstPayment',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'daysBetweenPayments',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'numberOfPayments',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'movePaymentsToTheEndOfMonth',
        type: '',
        align: 'center',
        width: 'w-full',
        sortable: false,
        formatType: 'boolean',
      },
      {
        visible: true,
        position: 6,
        linkedField: 'daysOffsetPayments',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'hasEndOfMonth',
        width: 'w-full',
        formatType: 'tristate',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'name',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'generateName',
        formatType: 'custom',
        customComponent: TypeOfPaymentGenerateButton,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'daysToFirstPayment',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'daysBetweenPayments',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 1 },
        linkedField: 'numberOfPayments',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 3, y: 1 },
        linkedField: 'daysOffsetPayments',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'movePaymentsToTheEndOfMonth',
        type: 'boolean',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      name: z
        .string({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      daysToFirstPayment: z
        .number({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .max(180, { message: 'max_num_180' }),
      daysBetweenPayments: z
        .number({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .max(180, { message: 'max_num_180' }),
      numberOfPayments: z
        .number({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(1, { message: 'min_num_rate_1' })
        .max(12, { message: 'max_num_rate_12' }),
      movePaymentsToTheEndOfMonth: z.boolean(),
      daysOffsetPayments: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .max(180, { message: 'max_num_180' }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default TypOfPaymentRepository;
